/**
 * Date-only helpers · established by ARC-011 (Calendar-Date Discipline).
 *
 * Business dates in this codebase — Invoice Date, Billing Date, Delivery
 * Date, Due Date, Price Effective Date, Brief Date — are CALENDAR DATES,
 * not moments in time. They carry no timezone semantics and must NEVER
 * be passed through `new Date("2026-08-01")` because JavaScript parses
 * that as UTC midnight; a browser in UTC-05 then renders it as
 * "Jul 31" — the exact one-day-offset bug that hit Driver Price Notice
 * and would have hit Receiving invoice dates next.
 *
 * Rules:
 *
 *   1. Always store and pass business dates as ISO date-only strings
 *      ("YYYY-MM-DD"). Never as Date objects, never as ISO timestamps.
 *   2. Render with `fmtCalendarDate(str, opts)` — reconstructs the date
 *      via `Date.UTC(y, m-1, d)` so `toLocaleDateString` prints the
 *      same calendar day everywhere.
 *   3. When comparing calendar dates use `cmpCalendarDate(a, b)` which
 *      is a plain string compare (ISO date-only strings sort naturally).
 *   4. TIMESTAMPS (created_at, uploaded_at, approved_at, sent_at) DO
 *      carry timezone semantics — use `new Date(iso).toLocaleString()`
 *      for those. Don't confuse the two.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;


/**
 * Format an ISO date-only string ("YYYY-MM-DD") into a human string
 * without any timezone shift.
 *
 * @param {string|null|undefined} iso    Calendar-date string.
 * @param {Intl.DateTimeFormatOptions} [opts]  Locale options. Defaults
 *          to "en-CA" with weekday · month · day · year.
 * @returns {string}   Formatted date, or "—" when the input is falsy /
 *          malformed.
 */
export function fmtCalendarDate(iso, opts) {
  if (!iso) return "—";
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d  = Number(m[3]);
  // Anchor to UTC midnight so `toLocaleDateString` reads it back as the
  // same day in every browser locale.
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.toLocaleDateString(
    "en-CA",
    opts || { weekday: "short", year: "numeric", month: "short", day: "numeric" },
  );
}


/**
 * Short-form calendar date (e.g. "Aug 1, 2026") — used inside tables.
 */
export function fmtCalendarDateShort(iso) {
  return fmtCalendarDate(iso, { year: "numeric", month: "short", day: "numeric" });
}


/**
 * Return today's calendar date in ISO date-only form using the
 * browser's local timezone. Use for comparing operational "today" (a
 * calendar concept) against stored calendar dates.
 */
export function todayIso() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}


/**
 * String-compare two ISO date-only values. `undefined`/`null` sort last.
 */
export function cmpCalendarDate(a, b) {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}


/** True when an input matches strict ISO date-only shape (YYYY-MM-DD). */
export function isCalendarDate(s) {
  return typeof s === "string" && ISO_DATE_RE.test(s);
}
